import { getUserInitials } from 'utils/user.util';
import { QaPhantomLimits, resolvePhantomSize } from 'utils/qaPhantom.util';

export const generateEmptyQaEntry = (currUser, scanner) => ({
  _id: 'new',
  deviceId: scanner._id,
  date: new Date().toISOString(),
  setupAccuracy: '',
  setupConsole: '',
  centerFreq: '',
  txGainAtten: '',
  geoAccuracyHf: '',
  geoAccuracyAp: '',
  geoAccuracyRl: '',
  spatialResUpper: '',
  spatialResLower: '',
  spokesNum: '',
  artifactEval: '',
  notes: '',
  initials: getUserInitials(currUser),
  enteredById: currUser._id,
  fieldStrength: scanner.fieldStrength,
  cfMin: scanner.cfMin,
  cfMax: scanner.cfMax,
  tgMin: scanner.tgMin,
  tgMax: scanner.tgMax,
  phantomSize: scanner.phantomSize,
  outFields: [],
});

/** Merge current device MRI limit fields so QA checks track device settings (phantom size, field strength, CF/TG). */
export const getQaLimitContext = (entry, device) => {
  if (!device) {
    return entry;
  }
  return {
    ...entry,
    phantomSize: device.phantomSize ?? entry.phantomSize,
    fieldStrength: device.fieldStrength ?? entry.fieldStrength,
    cfMin: device.cfMin ?? entry.cfMin,
    cfMax: device.cfMax ?? entry.cfMax,
    tgMin: device.tgMin ?? entry.tgMin,
    tgMax: device.tgMax ?? entry.tgMax,
  };
};

const checkCfBounds = (value, entryData) => {
  const { cfMax, cfMin } = entryData;
  if (!(cfMax && cfMin)) return false;

  const cfAvg = (cfMax + cfMin) / 2;
  const cfAltVar = 42.58 * 1.5 * entryData.fieldStrength;
  const cfAltMax = cfAvg + cfAltVar;
  const cfAltMin = cfAvg - cfAltVar;
  return (value > cfMax || value < cfMin) && (value > cfAltMax || value < cfAltMin);
};

const checkTgBounds = (value, entryData) => {
  const { tgMax, tgMin } = entryData;
  if (!(tgMax && tgMin)) return false;

  const tgAvg = (tgMax + tgMin) / 2;
  const tgPercentMax = tgAvg + 0.1 * tgAvg;
  const tgPercentMin = tgAvg - 0.1 * tgAvg;
  return (value > tgMax || value < tgMin) && (value > tgPercentMax || value < tgPercentMin);
};

export const checkForOutField = (field, value, entryData, device) => {
  const ctx = getQaLimitContext(entryData, device);
  let outOfBounds = false;

  switch (field) {
    case 'setupAccuracy':
      if (value > 5.0 || value < -5.0) outOfBounds = true;
      break;
    case 'setupConsole':
      outOfBounds = !value;
      break;
    case 'centerFreq':
      outOfBounds = checkCfBounds(value, ctx);
      break;
    case 'txGainAtten':
      outOfBounds = checkTgBounds(value, ctx);
      break;
    case 'geoAccuracyHf': {
      const phantomKey = resolvePhantomSize(ctx.phantomSize);
      const limit = QaPhantomLimits[phantomKey].geoHf;
      if (value > limit + 2 || value < limit - 2) outOfBounds = true;
      break;
    }
    case 'geoAccuracyAp':
    case 'geoAccuracyRl': {
      const phantomKey = resolvePhantomSize(ctx.phantomSize);
      const limit = QaPhantomLimits[phantomKey].geoApRl;
      if (value > limit + 2 || value < limit - 2) outOfBounds = true;
      break;
    }
    case 'spatialResUpper':
    case 'spatialResLower': {
      const phantomKey = resolvePhantomSize(ctx.phantomSize);
      const limit = QaPhantomLimits[phantomKey].spatialRes;
      if (value > limit) outOfBounds = true;
      break;
    }
    case 'artifactEval':
      outOfBounds = value;
      break;
    default:
      throw new Error(`Unrecognized field: ${field}`);
  }
  return outOfBounds;
};

const QA_OUTCHECK_FIELDS = [
  'setupAccuracy',
  'setupConsole',
  'centerFreq',
  'txGainAtten',
  'geoAccuracyHf',
  'geoAccuracyAp',
  'geoAccuracyRl',
  'spatialResUpper',
  'spatialResLower',
  'artifactEval',
];

/** Recompute stored outFields using current device limits (phantom size, field strength, CF/TG). */
export const computeQaOutFields = (entry, device) => {
  const out = [];
  QA_OUTCHECK_FIELDS.forEach((f) => {
    if (checkForOutField(f, entry[f], entry, device)) out.push(f);
  });
  return out;
};

export const getQaLimits = (limitSource) => {
  let cfLimit = null;
  let tgLimit = null;
  if (limitSource.cfMax) {
    const max = Math.round(limitSource.cfMax);
    const min = Math.round(limitSource.cfMin);
    cfLimit = `${max} to ${min} Hz`;
  }
  if (limitSource.tgMax) {
    const max = limitSource.tgMax.toFixed(2);
    const min = limitSource.tgMin.toFixed(2);
    tgLimit = `${max} to ${min} dB`;
  }
  return [cfLimit, tgLimit];
};

export const getSliceNum = (fieldStrength) => {
  let sliceNumber = '8';
  if (fieldStrength < 0.5) {
    sliceNumber = '11';
  } else if (fieldStrength < 0.6) {
    sliceNumber = '10';
  } else if (fieldStrength < 1.0) {
    sliceNumber = '9';
  }
  return sliceNumber;
};

export { QaPhantomLimits };
